import {
  Divider,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import flatten from 'lodash/flatten';
import values from 'lodash/values';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import MaxWidth from '@/components/common/layout/MaxWidth';
import Parks from '@/components/common/Parks';
import ErrorBoundary from '@/components/ErrorBoundary';
import Description from '@/components/localities/Description';
import { FAQs } from '@/components/localities/FAQs';
import { LocalityHead } from '@/components/localities/Head';
import HowToOutdoorsy from '@/components/localities/HowToOutdoorsy';
import InlineGuidedSearch from '@/components/localities/InlineGuidedSearch';
import NearbyContent from '@/components/localities/NearbyContent';
import NearbyRVRentalContent from '@/components/localities/NearbyRVRentalContent';
import RentalFilters from '@/components/localities/RentalFilters';
import { RichTextSection } from '@/components/localities/RichTextSection';
import { SearchQuerySection } from '@/components/localities/SearchQuerySection';
import SeeWhatsAvailable from '@/components/localities/SeeWhatsAvailable';
import NewsLetterSignup from '@/components/switchback/NewsLetterSignup/NewsLetterSignup';
import { ESearchFilters } from '@/constants/searchFilters';
import { SEO_VEHICLES } from '@/constants/vehicleDetails';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { trackEvent } from '@/services/track-event';

import { filtersToCamelCase, getHeroTitleNoLines } from './LocalityTemplate';
const vowels = ['a', 'e', 'i', 'o', 'u'];

const StateLocalityTemplate = props => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { keyword, metadata, localitySearchFilters } = props;
  const {
    title,
    breadcrumbs,
    region_code,
    top_cities,
    links,
    faqs,
    region_name,
    how_to_rent,
    introduction,
    sections,
    keywords,
    locale,
  } = metadata;

  const localityActiveFilters = filtersToCamelCase(localitySearchFilters);
  const rvType = SEO_VEHICLES[keyword?.slug] ? keyword.title : 'RV';
  const relatedLinks = links ? flatten(values(links)) : [];

  const howToRentTitle = title || 'Outdoorsy';
  const howToRentDescription = how_to_rent?.length > 0 ? how_to_rent[0].description : '';
  const heroTitleNoLines = getHeroTitleNoLines(title, 'state', title);

  useEffect(() => {
    dispatch(
      setHeaderSearchFilter({
        address: title,
        [ESearchFilters.SKIP_DEFAULTS]: true,
      }),
    );
  }, [dispatch, title]);

  useEffect(() => {
    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'State locality',
    });
  }, [dispatch]);

  return (
    <>
      <div className="flex flex-col">
        <div className="w-full py-0 pb-4 text-center lg:py-10 bg-canvas-100">
          <div className="px-4 pt-4 lg:pt-8">
            <div className="max-w-xl mx-auto">
              <MaxWidth className="mb-4 lg:mb-10">
                <Heading
                  variant={EHeadingStyleVariant.H4}
                  component="h1"
                  className="mb-4 lg:text-3xl">
                  {heroTitleNoLines}
                </Heading>
                <Text variant={ETextStyleVariant.Medium}>
                  <FormattedMessage
                    id="wPeUPO"
                    defaultMessage="Let's start with a few questions to narrow down your options."
                    description="Locality guided search > Header text explaining the guided search process"
                  />
                </Text>
              </MaxWidth>
            </div>
          </div>
          <InlineGuidedSearch
            initialParams={localityActiveFilters}
            searchTitle={heroTitleNoLines}
            locality={{
              name: title,
              address: title,
            }}
            source="guided_search_1_locality"
            guidedSearchData={props.guidedSearchData}
          />
        </div>
      </div>

      <div className="flex flex-col items-center">
        <LocalityHead cityName={title} regionCode={region_code} stateName={region_name} />

        <MaxWidth className={`px-4 md:pt-10`}>
          <Divider />
        </MaxWidth>

        {!keyword && introduction && (
          <MaxWidth className="py-10 gap-6">
            <div className="lg:max-w-60 lg:mx-auto">
              <Heading
                variant={EHeadingStyleVariant.H4}
                component="h2"
                className="mb-6 text-center">
                <FormattedMessage
                  defaultMessage="Discover {locality} RV rentals for memorable road trips"
                  values={{ locality: title }}
                  id="z+dE6i"
                />
              </Heading>

              <Description introduction={introduction} />
            </div>
          </MaxWidth>
        )}

        <MaxWidth className="py-8 mx-auto mb-8">
          <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
            <FormattedMessage defaultMessage="Find your personal RV rental style" id="7JH5c7" />
          </Heading>

          <SeeWhatsAvailable address={title} />
        </MaxWidth>

        <div className="w-full py-10 bg-canvas-100">
          <MaxWidth className="mx-auto">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              {vowels.includes(rvType.toLowerCase().charAt(0))
                ? intl.formatMessage(
                    { defaultMessage: 'How to rent an {rv_Type} in {locationName}', id: 'm+b88e' },
                    { rv_Type: rvType, locationName: howToRentTitle },
                  )
                : intl.formatMessage(
                    { defaultMessage: 'How to rent a {rv_Type} in {locationName}', id: 'bUUD8R' },
                    { rv_Type: rvType, locationName: howToRentTitle },
                  )}
            </Heading>

            <HowToOutdoorsy title={howToRentTitle} description={howToRentDescription} />
          </MaxWidth>
        </div>

        {!keyword && !!sections?.length && (
          <div className="w-full">
            {sections.map((section, index) => {
              const { section_type = 'rich_text', background = 'white', divider = false } = section;
              const { background: prevSectionBackground = 'white' } = sections[index - 1] || {};

              return (
                <ErrorBoundary key={index} fallBackComponent={null}>
                  <div
                    className={cn('w-full pb-10', {
                      ['pt-10']: !index || prevSectionBackground !== background,
                      ['bg-white']: background === 'white',
                      ['bg-canvas-100']: background === 'canvas',
                    })}>
                    <MaxWidth className="mx-auto">
                      {(section_type === 'search_query' && <SearchQuerySection {...section} />) || (
                        <RichTextSection {...section} />
                      )}

                      {divider && <Divider className="mt-10" />}
                    </MaxWidth>
                  </div>
                </ErrorBoundary>
              );
            })}
          </div>
        )}

        {!keyword && !!keywords?.length && (
          <MaxWidth className="py-10 gap-6">
            {!!keywords?.length && <RentalFilters keywords={keywords} />}
          </MaxWidth>
        )}

        <MaxWidth className="m-auto mt-12 mb-20">
          <NewsLetterSignup
            title={intl.formatMessage({
              defaultMessage:
                'Be the first to get doses of destination inspiration, and discount codes.',
              id: 'RdFytN',
            })}
            source="State locality"
          />
        </MaxWidth>

        <MaxWidth className="mb-10">
          <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
            <FormattedMessage
              defaultMessage="Frequently asked questions about renting an RV near {locality}"
              id="SESQZp"
              values={{ locality: title }}
            />
          </Heading>

          <FAQs items={faqs} rvType={rvType} />
        </MaxWidth>

        <div className="w-full py-10 mb-10 bg-canvas-100">
          <MaxWidth collapseMobile id="rentals" className="mx-auto">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage defaultMessage="Discover the best of what's around" id="S0ya4u" />
            </Heading>

            <Parks locale={locale} />
          </MaxWidth>
        </div>

        <MaxWidth className="mb-10">
          <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
            <FormattedMessage defaultMessage="Popular Nearby Cities" id="frI5QK" />
          </Heading>

          <div className="flex-row w-full xs:flex xs:flex-wrap">
            <NearbyRVRentalContent rvType={rvType} locations={top_cities} />
          </div>
        </MaxWidth>

        {!!relatedLinks.length && (
          <MaxWidth className="mb-10">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage defaultMessage="Top RV Parks and Campgrounds" id="cWzzHf" />
            </Heading>

            <div className="flex-row w-full xs:flex xs:flex-wrap">
              <NearbyContent locations={relatedLinks} />
            </div>
          </MaxWidth>
        )}

        <MaxWidth className="mb-10">
          <div className="flex justify-center">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        </MaxWidth>
      </div>
    </>
  );
};

export default StateLocalityTemplate;
